// One card per sub-feature, each carrying its reference.
//
// This strip answers to one document and only that one. A card mixing another
// PRD's components could not be checked against either, because a reader
// cannot tell a missing component from one that belongs elsewhere.
import React from 'react';
import { FiArrowRight, FiLock } from 'react-icons/fi';
import s from '../KeywordPerformance.module.css';
import { num, pct } from '../format';

// The funnel stages, in order, with the words the backend uses for them.
const STAGE_LABEL = {
  visibility: 'Not being shown', attraction: 'Shown, not clicked',
  page: 'Clicked, not added', checkout: 'Added, not bought',
  holds: 'Holding share', null: 'Not enough data',
};

function Movers({ climbers, fallers }) {
  const rows = [...(climbers || []), ...(fallers || [])];
  if (!rows.length) return null;
  return (
    <ul className={s.cardList}>
      {rows.map((m) => (
        <li key={`${m.term}-${m.places}`}>
          <span className={s.cardTerm}>{m.term}</span>
          <span className={m.places > 0 ? s.up : s.down}>
            {m.places > 0 ? '▲' : '▼'} {Math.abs(m.places)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Body({ card }) {
  switch (card.key) {
    case 'ranking':
      return <Movers climbers={card.climbers} fallers={card.fallers} />;

    case 'volume':
      if (card.rising == null) return null;
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Rising</span>
            <span className={s.up}>{num(card.rising)} terms</span>
          </li>
          <li>
            <span className={s.cardTerm}>Falling</span>
            <span className={s.down}>{num(card.falling)} terms</span>
          </li>
          <li className={s.cardNote}>
            Counted only on the {num(card.comparable)} terms present in both windows — a
            term new to your list has nothing to have grown from.
          </li>
        </ul>
      );

    case 'conversion': {
      if (!card.stages) return null;
      const stages = Object.entries(card.stages)
        .filter(([k]) => k !== 'holds' && k !== 'null' && k !== 'None')
        .sort((a, b) => b[1] - a[1]);
      if (!stages.length) return null;
      return (
        <ul className={s.cardList}>
          {stages.map(([stage, n]) => (
            <li key={stage}>
              <span className={s.cardTerm}>{STAGE_LABEL[stage] || stage}</span>
              <span className={s.cardFigure}>{num(n)}</span>
            </li>
          ))}
          <li className={s.cardNote}>
            Price reads on {num(card.priced)} terms and delivery speed on{' '}
            {num(card.delivery_readable)} — a term with no purchases on either side has no
            converting price to compare with.
          </li>
        </ul>
      );
    }

    case 'opportunity':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((t) => (
            <li key={t.term}>
              <span className={s.cardTerm}>{t.term}</span>
              <span className={s.cardFigure}>
                {num(t.volume)} <span className={s.muted}>searches · {pct(t.purchase_share, 0)} yours</span>
              </span>
            </li>
          ))}
          {card.absent && (
            <li className={s.cardNote}>
              {num(card.absent.total)} more terms you get no impressions on at all —{' '}
              {card.absent.split_covers_all ? '' : 'at least '}
              {num(card.absent.competitor_wins_it)} held by a competitor you named.
            </li>
          )}
        </ul>
      );

    case 'gap':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((t) => (
            <li key={t.term}>
              <span className={s.cardTerm}>{t.term}</span>
              <span className={s.cardFigure}>
                <strong>{t.competitor}</strong> {pct(t.theirs, 0)}{' '}
                <span className={s.muted}>vs {pct(t.yours, 0)}</span>
              </span>
            </li>
          ))}
        </ul>
      );

    case 'score':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((t) => (
            <li key={t.term}>
              <span className={s.cardTerm}>{t.term}</span>
              <span className={s.cardFigure}>{Math.round(t.score)}</span>
            </li>
          ))}
          <li className={s.cardNote}>
            Weights are fitted on your own keyword history against what predicts purchases,
            not chosen. They are stated above so the number can be argued with.
          </li>
        </ul>
      );

    default:
      return null;
  }
}

export default function FeatureCards({ data, loading, onOpen }) {
  if (loading && !data) {
    return (
      <div className={s.cardGrid}>
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={s.cardSkeleton} />)}
      </div>
    );
  }
  if (!data?.cards?.length) return null;

  return (
    <div className={s.cardGrid}>
      {data.cards.map((card) => {
        const clickable = card.state !== 'unavailable';
        const open = () => onOpen(card.key);
        return (
          <section key={card.key}
                   className={`${s.card} ${s[`card_${card.state}`]} ${clickable ? s.cardOpen : ''}`}
                   onClick={clickable ? open : undefined}
                   role={clickable ? 'button' : undefined}
                   tabIndex={clickable ? 0 : undefined}
                   onKeyDown={clickable ? (e) => {
                     if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                   } : undefined}>
            <header className={s.cardHead}>
              <span className={s.cardRef}>{card.ref}</span>
              {clickable ? <FiArrowRight className={s.cardGo} /> : <FiLock className={s.cardLock} />}
            </header>
            <h3 className={s.cardTitle}>{card.title}</h3>
            <p className={s.cardQuestion}>{card.question}</p>
            <div className={s.cardHeadline}>
              <span className={s.cardValue}>{card.headline}</span>
              {card.unit && <span className={s.cardUnit}>{card.unit}</span>}
            </div>
            <p className={s.cardDetail}>{card.detail}</p>
            <Body card={card} />
          </section>
        );
      })}
    </div>
  );
}
